import Breadcrumb from 'components/Breadcrumb';
import CustomTabv2 from 'components/custom/CustomTabv2';
import React, { MouseEvent, ReactNode } from 'react';
import ApiAccessComponent from './ApiAccessComponent';
import ApiEditorComponent from './editor/ApiEditorComponent';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { RootState } from '../../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { changeApiVariable, changeApiVariableDirty, changeIsApiVariableUpdate, clearApiVariable } from '../../../redux/actions/ApiVariableActions';
import ComponentName from '../../../constant/ComponentName';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import CustomEditorItemComponent from '../../custom/CustomEditorItemComponent';
import ApiVariableService from '../../../service/ApiVariableService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import { ApiVariable } from '../../../domain/enrichment/EnrichmentDomain';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../../deploy/UndeployImpactDialog';
import LanguageUtil from '../../../util/LanguageUtil';
import RoleHelper from '../../../RoleHelper';
import ComponentAuditLog from '../../ComponentAuditLog';
import CustomLoading from '../../custom/CustomLoading';
import { clearPage } from 'redux/actions/PageActions';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../../helpers/StandardEditorContainerComponent';
import ComponentErrorLog from '../../ComponentErrorLog';
import { isStringValid } from '../../../util/Generator';
import MessageUtil from '../../../util/MessageUtil';
import { EnrichedComponentCategory } from '../../../domain/category/CategoryOperationDomains';
import { ConfirmPopup } from 'primereact/confirmpopup';
import eq = query.eq;

class ApiEditorContainer extends StandardEditorContainerComponent<Props, State, ApiVariable> {
  public state: State = new State();

  private apiVariableSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly apiVariableService: ApiVariableService = ServiceContainer.getInstance().get(ServiceNames.API_VARIABLE);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.ENRICHMENT,
      icon: 'enrichment',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const {
      apiVariable: { apiVariable, isDirty },
      isUpdate,
    } = this.props;
    const { loading } = this.state;

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'flex flex-col h-full w-full overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={apiVariable?.name}
          description={apiVariable?.description ? apiVariable.description : ''}
          status={apiVariable?.stage || 'DRAFT'}
          component={ComponentName.API_VARIABLE}
          isUpdate={isUpdate}
          icon={'enrichment'}
          onChangeName={this.onChangeName}
          onChangeDescription={this.onChangeDescription}
          onSave={this.saveApiVariable}
          onDeploy={this.deployApiVariable}
          onUndeploy={this.undeployApiVariable}
          onRemove={this.removeApiVariable}
          disableSave={!RoleHelper.canEdit('EnrichmentApi')}
          disableRemove={!RoleHelper.canEdit('EnrichmentApi')}
          disableNameChange={!RoleHelper.canEdit('EnrichmentApi')}
          disableDescriptionChange={!RoleHelper.canEdit('EnrichmentApi')}
          disableDeploy={!RoleHelper.canDeploy('EnrichmentApi') || isDirty}
          disableUndeploy={!RoleHelper.canDeploy('EnrichmentApi')}
        />
        <div className={'flex flex-col overflow-y-scroll h-full w-full py-3 '}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearApiVariable } = this.props;
    clearApiVariable();
    super.componentWillUnmount();
  }

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  protected _startTracker = async () => {
    const variableId = this.getItemIdFromRoute();
    if (variableId == undefined) {
      this.setState({ loading: false });
    }
    const apiVariableQuery = query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).where(eq('name', variableId));
    this.apiVariableSubscription = subscribe.Formica.subscribe(CollectionNames.ENRICHMENTS, apiVariableQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.apiVariableSubscription]);
  };

  protected tracker = async () => {
    const { changeIsApiVariableUpdate, changeApiVariable, changeApiVariableDirty } = this.props;
    if (this.apiVariableSubscription?.ready()) {
      const variableId = this.getItemIdFromRoute();
      if (variableId == undefined) {
        changeIsApiVariableUpdate(false);
        changeApiVariableDirty(false);
        RoleHelper.setCurrentAccess('Edit');
        this.setState({ loading: false });
        return;
      }
      const apiVariableQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).where(eq('name', variableId)).build();
      const apiVariable: ApiVariable | undefined = await this.repository.find(apiVariableQuery);
      if (apiVariable == undefined) return;
      changeApiVariable(apiVariable);
      changeApiVariableDirty(false);
      RoleHelper.setCurrentAccess(apiVariable);
      this.setState({ loading: false });
    }
  };

  protected _stopTracker = async () => {
    await this.apiVariableSubscription?.stop();
    this.computation?.stop();
  };

  protected getItemIdFromRoute = () => {
    const { changeIsApiVariableUpdate } = this.props;
    const id = this.props.router.params.id;
    changeIsApiVariableUpdate(id != undefined);
    return id;
  };

  private onChangeName = (name: string) => {
    const {
      changeApiVariable,
      apiVariable: { apiVariable },
    } = this.props;
    if (isStringValid(name)) {
      apiVariable.name = name.trim();
      changeApiVariable(apiVariable);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDescription = (description: string) => {
    const {
      changeApiVariable,
      apiVariable: { apiVariable },
    } = this.props;
    apiVariable.description = description;
    changeApiVariable(apiVariable);
  };

  private switchToUpdate = async () => {
    const {
      apiVariable: { apiVariable },
      router: { navigate, params },
      enterComponent,
    } = this.props;
    if (apiVariable.name == '') return;
    enterComponent({ component: ComponentName.ENRICHMENT, id: apiVariable.name });
    navigate(`/enrichment${params.folderId ? `/folder/${params.folderId}` : ''}/api/${apiVariable.name}`, { replace: true });
    setTimeout(() => {
      this.resubscribeTracker();
    });
  };

  private saveApiVariable = async () => {
    const {
      apiVariable: { apiVariable },
      isUpdate,
      changeIsApiVariableUpdate,
      lastCategory,
    } = this.props;
    const isSaved = await this.apiVariableService.save(apiVariable, isUpdate, lastCategory);
    if (isSaved) {
      changeIsApiVariableUpdate(true);
      if (!isUpdate) await this.switchToUpdate();
    }
  };

  private undeployApiVariable = async (e: MouseEvent<HTMLElement>) => {
    const {
      apiVariable: { apiVariable },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: () => confirmUndeployWithImpact(ComponentName.API_VARIABLE, [apiVariable.name], () => this.apiVariableService.undeploy(apiVariable.name)),
      message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
    });
  };

  private deployApiVariable = async (e: MouseEvent<HTMLElement>) => {
    const {
      apiVariable: { apiVariable },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await this.apiVariableService.save(apiVariable, true, {} as EnrichedComponentCategory);
        await this.apiVariableService.deploy(apiVariable);
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
    });
  };

  private removeApiVariable = async (e: MouseEvent<HTMLElement>) => {
    const {
      apiVariable: { apiVariable },
      router: { navigate },
    } = this.props;
    try {
      customConfirm({
        target: e.currentTarget,
        accept: async () => {
          const success = await this.apiVariableService.delete(apiVariable.name);
          if (success) {
            navigate(`/enrichment`);
          }
        },
        message: LanguageUtil.get({ id: 'messages.confirm.confirmRemove', defaultMessage: 'Are you sure you want to remove?' }),
      });
    } catch (e) {
      console.error(e);
    }
  };

  protected getTabs = () => {
    return [
      {
        key: 'Editor',
        label: LanguageUtil.get({ id: 'common.tabs.editor', defaultMessage: 'Editor' }),
        content: <ApiEditorComponent />,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <ApiAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.API_VARIABLE, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.API_VARIABLE} documentName={this.props.apiVariable.apiVariable.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.API_VARIABLE, true) && this.environmentUtil.isAuditLogVisible(),
      },
      {
        key: 'Error',
        label: 'Error',
        content: <ComponentErrorLog component={ComponentName.API_VARIABLE} documentName={this.props.apiVariable.apiVariable.name} />,
        visible: RoleHelper.canManage(ComponentName.API_VARIABLE, true),
      },
    ];
  };

  protected changeItem = (item: ApiVariable) => {
    const { changeApiVariable } = this.props;
    changeApiVariable(item);
  };
}

interface Props extends FormicaProps, ReduxProps {}

class State {
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  apiVariable: state.apiVariable,
  isUpdate: state.apiVariable.isUpdate,
  lastCategory: state.page.lastCategory,
});

const mapDispatchToProps = {
  changeIsApiVariableUpdate,
  changeApiVariable,
  clearApiVariable,
  enterComponent,
  leaveComponent,
  clearPage,
  changeApiVariableDirty,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ApiEditorContainer));
